import React, { useState, ChangeEvent, useRef } from 'react'

import Button from '../../../scentregroup-ui/components/form/button'
import Form from '../../../scentregroup-ui/components/form/form'
import Textfield from '../../../scentregroup-ui/components/form/textfield'
import Row from '../../../scentregroup-ui/components/form/row'

import useAuth0ClientConfig from '../../../lib/auth0/use-auth0-client-config'
import {
  Result,
  createSuccessResult,
  createErrorResult,
} from '../../../lib/types/result'
import { isEmptyString } from '../../../lib/validators'
import { createFormValidator } from '../../../lib/formValidation'
import useAnalytics from '../../../lib/analytics/use-analytics'
import {
  trackPSteps,
  signUpSteps,
  logError,
  trackVerificationCode,
} from '../../../lib/analytics/events'
import {
  ValidateCodeError,
  ValidateCode,
  ResendCode,
  ResendCodeError,
} from '../../../lib/apiService'
import OnboardingForm from '../onboarding-form'
import style from './index.scss'

const CODE_IS_EMPTY =
  'Please enter a valid code, check your email to make sure you’re using the correct code'
const ERRORS = {
  CODE_IS_EMPTY,
  CODE_IS_INVALID: CODE_IS_EMPTY,
}

const validateCode = (code: string): Result<string> => {
  if (isEmptyString(code)) {
    return createErrorResult(ERRORS.CODE_IS_EMPTY)
  }

  return createSuccessResult()
}

export interface Values extends Record<string, unknown> {
  code: string
}

interface OnFailureData {
  email: string
  code: string
  errors: ValidateCodeError[]
}
export interface OnFailure {
  (data: OnFailureData): void
}

interface OnResendCodeSuccessData {
  email: string
}
export interface OnResendCodeSuccess {
  (data: OnResendCodeSuccessData): void
}

interface OnResendCodeErrorData extends OnResendCodeSuccessData {
  errors: ResendCodeError[]
}
export interface OnResendCodeError {
  (data: OnResendCodeErrorData): void
}

export interface Props {
  email: string
  actionName: string
  onFailure: OnFailure
  onResendCodeSuccess: OnResendCodeSuccess
  onResendCodeError: OnResendCodeError
  validateCode: ValidateCode
  resendCode: ResendCode
}

const VerifyCodeForm: React.FC<Props> = (props) => {
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [isBlurValidationEnabled, setIsBlurValidationEnabled] = useState(true)
  const [isResendingCode, setIsResendingCode] = useState(false)
  const codeInput = useRef<HTMLInputElement>(null)
  const { dataLayer } = useAnalytics()
  const { cookieDomain } = useAuth0ClientConfig()

  const validateForm = createFormValidator<Values>(
    { code },
    (fieldName, errorValue) => {
      setCodeError(errorValue)
      if (errorValue) {
        dataLayer(logError('Verification Code Validation Error', errorValue))
      }
    },
    () => [
      {
        validationFn: validateCode,
        fieldName: 'code',
        fieldElement: codeInput.current,
      },
    ]
  )

  const onSubmit = async (e: ChangeEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const validationResult = validateForm()

    if (!validationResult.success) {
      return
    }

    setIsValidatingCode(true)
    dataLayer(
      props.actionName === 'create-password' ? trackPSteps(1) : signUpSteps(2)
    )
    const result = await props.validateCode(
      props.email,
      code.replace(/\D/g, ''),
      props.actionName,
      cookieDomain
    )
    setIsValidatingCode(false)

    if (result.success) {
      // nothing else to do: validateCode(...) will redirect automatically on success
      trackVerificationCode(1)
      return
    }

    let formError = ''
    const systemErrors: ValidateCodeError[] = []

    for (const resultError of result.error) {
      switch (resultError) {
        case ValidateCodeError.Mismatch:
        // falls through
        case ValidateCodeError.Rejected:
        // falls through
        case ValidateCodeError.Invalid:
          formError = ERRORS.CODE_IS_INVALID
          break
        default:
          systemErrors.push(resultError)
      }
    }

    if (formError) {
      setCodeError(formError)
      dataLayer(logError('Verification Code Error', formError))
    }

    if (systemErrors.length > 0) {
      props.onFailure({
        email: props.email,
        code,
        errors: systemErrors,
      })
      dataLayer(logError('Verification Code Error', systemErrors.toString()))
    }
  }

  const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Without this guard, when a field is on focus and the submit button is
    // clicked, the onBlur validation may cause the height of the form to
    // change, making it so that the click lands outside of the submit button!
    // This is an issue in DGC forms at the time of this writing.
    // todo(jcejudo): create ticket for above scenario and link to it
    if (!isBlurValidationEnabled) {
      return
    }

    validateForm(e.target.name)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
  }

  const disableBlurValidation = () => setIsBlurValidationEnabled(false)
  const enableBlurValidation = () => setIsBlurValidationEnabled(true)

  const onClickResendCode = async () => {
    codeInput.current?.focus()
    setIsResendingCode(true)
    const result = await props.resendCode(props.email)
    setIsResendingCode(false)

    if (result.success) {
      dataLayer(trackPSteps(2))
      return props.onResendCodeSuccess({ email: props.email })
    }

    const systemErrors: ResendCodeError[] = []

    for (const resultError of result.error) {
      systemErrors.push(resultError)
    }

    props.onResendCodeError({
      email: props.email,
      errors: systemErrors,
    })
  }

  return (
    <OnboardingForm
      title="Verification code"
      intro={
        <>
          We’ve sent you a verification code to{' '}
          <strong className={style.email}>{props.email}</strong>
        </>
      }
      form={
        <Form onSubmit={onSubmit} noValidate>
          <Row>
            <Textfield
              innerRef={codeInput}
              label="Verification code"
              name="code"
              id="code"
              value={code}
              pattern="(/d|/s)*"
              inputMode="numeric"
              autoComplete="one-time-code"
              errorMessage={codeError}
              isHorizontal={false}
              onChange={onChange}
              onBlur={onBlur}
            />
          </Row>

          <Row>
            <div
              data-testid="submit-button-wrapper"
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              <Row>
                <Button
                  buttonType="submit"
                  disabled={isValidatingCode}
                  loading={isValidatingCode}
                >
                  Continue
                </Button>
              </Row>
            </div>
          </Row>

          <Row>
            <div
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              <Row>
                <Button
                  type="inferior"
                  buttonType="button"
                  loading={isResendingCode}
                  onClick={onClickResendCode}
                >
                  Resend verification code
                </Button>
              </Row>
            </div>
          </Row>
        </Form>
      }
    />
  )
}

export default VerifyCodeForm
